<template>
  <e-search
    v-model="modelValue"
    :label="label"
    :disabled="disabled"
    @close="onClickClose"
  />
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import useNotificationStore from '@/stores/notification';
  import googleMapsApi from '@/compositions/google-maps-api';
  import eSearch from '@/elements/e-search.vue';

  const COMPONENT_RESTRICTIONS = {
    country: 'CH'
  };

  type Setup = {
    googleMapsApi: ReturnType<typeof googleMapsApi>;
    notificationStore: ReturnType<typeof useNotificationStore>;
  }

  type Data = {
    autocompleteInstance?: google.maps.places.Autocomplete;
    geocoder?: google.maps.Geocoder;
  }

  /**
   * Renders a search field witch google autocomplete for geographical places.
   */
  export default defineComponent({
    name: 'c-google-places-autocomplete',
    components: {
      eSearch,
    },

    // components: {},

    props: {
      /**
       * Allows to show a label for the field.
       */
      label: {
        type: String,
        default: undefined,
      },

      /**
       * Allows to set a query.
       */
      query: {
        type: String,
        default: '',
      },

      /**
       * Gets an optional initial postal code which loads the matching city and fills the input value.
       */
      initialPostalCode: {
        type: String,
        default: '',
      },

      /**
       * Allows disabling the autocomplete.
       */
      disabled: {
        type: Boolean,
        default: undefined,
      },
    },
    emits: {
      'change': (payload: google.maps.GeocoderResult) => !!payload,
      'update:modelValue': (payload: string) => typeof payload !== 'undefined',
      'reset': () => true,
    },

    setup(): Setup {
      return {
        googleMapsApi: googleMapsApi(),
        notificationStore: useNotificationStore(),
      };
    },
    data(): Data {
      return {
        autocompleteInstance: undefined,
        geocoder: undefined,
      };
    },

    computed: {
      modelValue: {
        get() {
          return this.query;
        },
        set(value: string) {
          this.$emit('update:modelValue', value);
        },
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.googleMapsApi.then((maps) => {
        this.createGeoCoderInstance(maps);
        this.createAutocompleteInstance(maps);

        if (this.initialPostalCode) {
          this.setInitialPlace();
        }
      });
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      createAutocompleteInstance(maps: typeof google.maps): void {
        const options = {
          types: ['(regions)'],
          componentRestrictions: COMPONENT_RESTRICTIONS,
        };

        this.autocompleteInstance = new maps.places.Autocomplete(this.$el.querySelector('input'), options);

        this.autocompleteInstance.addListener('place_changed', this.onPlaceChanged);
      },

      createGeoCoderInstance(maps: typeof google.maps) {
        this.geocoder = new maps.Geocoder();
      },

      onPlaceChanged() {
        const place = this.autocompleteInstance?.getPlace();
        const location = place?.geometry?.location;
        const address = location ? undefined : place?.name;
        const componentRestrictions = location ? undefined : COMPONENT_RESTRICTIONS;

        this.geocoder?.geocode({ location, address, componentRestrictions }, (results) => {
          const resultWithPostalCode = results?.find(result => result.address_components
            .some(component => component.types.includes('postal_code')));

          if (resultWithPostalCode) {
            this.modelValue = place?.formatted_address ?? resultWithPostalCode.formatted_address;

            this.$emit('change', resultWithPostalCode);
          } else {
            this.modelValue = '';
            this.notificationStore.showNotification({
              type: 'info',
              message: this.$t('c-google-places-autocomplete.locationNotFound'),
              expire: true,
            });
          }
        });
      },

      setInitialPlace(): void {
        this.geocoder?.geocode({
          address: `${this.initialPostalCode}`,
          componentRestrictions: COMPONENT_RESTRICTIONS,
        }, (results) => {
          if (results?.[0]) {
            this.modelValue = this.getFormattedGeoCodeAddress(results[0]);
          }
        });
      },

      getFormattedGeoCodeAddress(result: google.maps.GeocoderResult): string {
        const { address_components: addressComponents } = result;
        const postalCode = addressComponents
          .find(component => component.types.includes('postal_code'))?.long_name;
        const city = addressComponents
          .find(component => component.types.includes('locality') && component.types.includes('political'))?.long_name;

        return `${postalCode} ${city}`;
      },

      onClickClose(): void {
        this.modelValue = '';
        this.$emit('reset');
      }
    },
    // render() {},
  });
</script>

<style lang="scss">
  // @use '@/setup/scss/mixins';
  // @use '@/setup/scss/variables';

  .c-google-places-autocomplete {
    // ...
  }
</style>
